import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import request from "../../../Helpers/API";
import PlantillaScreen from "../../helpers/PlantillaScreen";
import { CheckAllIcon, ChevronRight, SaveIcon, SearchIcon, UserIcon, XIcon } from "../Helper/Icons";
import { LoaderScreen } from "../../components/LoaderScreen";
import { capitalizePalabras } from "../../../Helpers/CapitalizePalabras";

export const AsignacionPasillosModal = ({ open, onClose, folio, onActualizar }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [auditores, setAuditores] = useState([]);
    const [pasillos, setPasillos] = useState([]);
    const [asignaciones, setAsignaciones] = useState({});
    const [auditorActivo, setAuditorActivo] = useState(null);
    const [searchPasillo, setSearchPasillo] = useState("");

    // ── Cargar datos al abrir ──────────────────────────────────────────────────
    useEffect(() => {
        if (!open || !folio) return;
        setLoading(true);
        setAuditorActivo(null);

        request
            .get(`ProgramarInventario/AsignacionPasillosListado?folio=${folio}`)
            .then((result) => {
                const auds = (result.auditores ?? []).map(a => ({
                    idusuario: a.idusuario,
                    usuario: a.usuario,
                    iniciales: a.usuario?.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase(),
                }));

                const pass = result.pasillos ?? [];

                const mapaInicial = {};
                pass.forEach(p => { mapaInicial[p.idpasillo] = null; });
                (result.asignaciones ?? []).forEach(a => {
                    mapaInicial[a.idpasillo] = a.idusuario;
                });

                setAuditores(auds);
                setPasillos(pass);
                setAsignaciones(mapaInicial);

                if (auds.length > 0) setAuditorActivo(auds[0].idusuario);
            })
            .catch((error) => {
                Swal.fire("ASIGNACIÓN PASILLOS", JSON.stringify(error), "error");
            })
            .finally(() => setLoading(false));
    }, [open, folio]);

    // ── Todos los hooks ANTES del return condicional ───────────────────────────

    const pasillosFiltrados = useMemo(() =>
        pasillos.filter(p =>
            String(p.idpasillo).toLowerCase().includes(searchPasillo.toLowerCase())
        ),
        [pasillos, searchPasillo]
    );

    const pasillosDisponibles = useMemo(() =>
        pasillos.filter(p =>
            asignaciones[p.idpasillo] === null ||
            asignaciones[p.idpasillo] === auditorActivo
        ),
        [pasillos, asignaciones, auditorActivo]
    );

    const todosDisponiblesAsignados = useMemo(() =>
        pasillosDisponibles.length > 0 &&
        pasillosDisponibles.every(p => asignaciones[p.idpasillo] === auditorActivo),
        [pasillosDisponibles, asignaciones, auditorActivo]
    );

    const pasillosLibres = useMemo(() =>
        pasillos.filter(p => asignaciones[p.idpasillo] === null).length,
        [pasillos, asignaciones]
    );

    const contadorPorAuditor = (idusuario) =>
        pasillos.filter(p => asignaciones[p.idpasillo] === idusuario).length;

    const togglePasillo = (idpasillo) => {
        const actual = asignaciones[idpasillo];
        if (actual === auditorActivo) {
            setAsignaciones(prev => ({ ...prev, [idpasillo]: null }));
        } else if (actual === null) {
            setAsignaciones(prev => ({ ...prev, [idpasillo]: auditorActivo }));
        }
    };

    const seleccionarTodos = () => {
        setAsignaciones(prev => {
            const next = { ...prev };
            pasillosDisponibles.forEach(p => {
                next[p.idpasillo] = todosDisponiblesAsignados ? null : auditorActivo;
            });
            return next;
        });
    };

    const handleGuardar = () => {
        // if (pasillosLibres > 0) {
        //     Swal.fire(
        //         "ASIGNACIÓN PASILLOS",
        //         `Quedan ${pasillosLibres} pasillos sin asignar. Asígnelos antes de guardar.`,
        //         "warning"
        //     );
        //     return;
        // }

        setSaving(true);

        const asignacionesArray = Object.entries(asignaciones)
            .filter(([_, idusuario]) => idusuario !== null)
            .map(([idpasillo, idusuario]) => ({
                idpasillo,
                idusuario,
            }));

        const post = {
            folio,
            asignaciones: JSON.stringify(asignacionesArray),
        };


        request
            .post("ProgramarInventario/GuardarAsignacionPasillos", post)
            .then(async (result) => {
                setSaving(false);
                if (result.resultado === 1) {
                    await Swal.fire("ASIGNACIÓN PASILLOS", result.mensaje, "error");
                } else {
                    await Swal.fire("ASIGNACIÓN PASILLOS", "Asignación guardada con éxito", "success");
                    onActualizar?.();

                    if (pasillosLibres < 1) {
                        onClose?.();
                    }
                }
            })
            .catch((error) => {
                setSaving(false);
                Swal.fire("ASIGNACIÓN PASILLOS", JSON.stringify(error), "error");
            });
    };

    const handleBackdropClick = (e) => {
        setSearchPasillo("")
        if (e.target === e.currentTarget) onClose?.();
    };

    // ── Early return DESPUÉS de todos los hooks ────────────────────────────────
    if (!open) return null;

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="ram-backdrop" onClick={handleBackdropClick}>
            <PlantillaScreen
                titulo={"Asignación de pasillos"}
                className={"ram-modal apm-modal"}
            >
                <button className="ram-close" onClick={onClose} type="button">X</button>
                <LoaderScreen loader={loading || saving} />

                {/* ── Resumen ── */}
                <div className="apm-summary">
                    <span className="apm-summary__pill apm-summary__pill--free">
                        {pasillosLibres} sin asignar
                    </span>
                    <span className="apm-summary__pill apm-summary__pill--total">
                        {pasillos.length} pasillos totales
                    </span>
                </div>

                {/* ── Layout dos columnas ── */}
                <div className="apm-layout">

                    {/* ── Panel izquierdo: auditores ── */}
                    <div className="apm-auditores">
                        <div className="apm-panel__header">Auditores</div>
                        <div className="apm-auditores__list">
                            {auditores.map(aud => {
                                const count = contadorPorAuditor(aud.idusuario);
                                const activo = auditorActivo === aud.idusuario;
                                return (
                                    <div
                                        key={aud.idusuario}
                                        className={`apm-auditor-row ${activo ? "apm-auditor-row--active" : ""}`}
                                        onClick={() => {
                                            setAuditorActivo(aud.idusuario);
                                            setSearchPasillo("");  // ← limpiar búsqueda al cambiar auditor
                                        }}
                                    >
                                        <div className="apm-auditor-row__avatar">{aud.iniciales}</div>
                                        <div className="apm-auditor-row__info">
                                            <span className="apm-auditor-row__nombre">{aud.usuario}</span>
                                            <span className="apm-auditor-row__count">
                                                {count > 0 ? `${count} pasillo${count !== 1 ? "s" : ""}` : "Sin pasillos"}
                                            </span>
                                        </div>
                                        {activo && (
                                            <div className="apm-auditor-row__arrow"><ChevronRight /></div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* ── Panel derecho: pasillos ── */}
                    <div className="apm-pasillos">
                        {auditorActivo ? (
                            <>
                                <div className="apm-panel__header">
                                    <span>Pasillos</span>
                                    <button
                                        type="button"
                                        className="apm-btn-todos"
                                        onClick={seleccionarTodos}
                                    >
                                        <CheckAllIcon />
                                        {todosDisponiblesAsignados
                                            ? "Deseleccionar disponibles"
                                            : "Seleccionar disponibles"}
                                    </button>
                                </div>

                                {/* Buscador — debajo del header, fuera del scroll */}
                                <div className="apm-search">
                                    <SearchIcon />
                                    <input
                                        type="text"
                                        className="apm-search__input"
                                        placeholder="Buscar pasillo..."
                                        value={searchPasillo}
                                        onChange={e => setSearchPasillo(e.target.value)}
                                    />
                                    {searchPasillo && (
                                        <button
                                            type="button"
                                            className="apm-search__clear"
                                            onClick={() => setSearchPasillo("")}
                                        >
                                            <XIcon />
                                        </button>
                                    )}
                                </div>

                                <div className="apm-pasillos__list">
                                    {pasillosFiltrados.length === 0 ? (
                                        <div className="apm-empty">
                                            {searchPasillo ? `Sin resultados para "${searchPasillo}"` : "Sin pasillos registrados."}
                                        </div>
                                    ) : (
                                        pasillosFiltrados.map(p => {
                                            const asignadoA = asignaciones[p.idpasillo];
                                            const esDelActivo = asignadoA === auditorActivo;
                                            const esDeOtro = asignadoA !== null && asignadoA !== auditorActivo;
                                            const audOtro = esDeOtro
                                                ? auditores.find(a => a.idusuario === asignadoA)
                                                : null;

                                            return (
                                                <label
                                                    key={p.idpasillo}
                                                    className={`apm-pasillo-row ${esDelActivo ? "apm-pasillo-row--checked" : ""} ${esDeOtro ? "apm-pasillo-row--disabled" : ""}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="apm-pasillo-row__cb"
                                                        checked={esDelActivo}
                                                        disabled={esDeOtro}
                                                        onChange={() => togglePasillo(p.idpasillo)}
                                                    />
                                                    <span className="apm-pasillo-row__check" />
                                                    <span className="apm-pasillo-row__nombre">{capitalizePalabras(String(p.idpasillo))}</span>
                                                    {esDeOtro && (
                                                        <span className="apm-pasillo-row__ocupado" title={audOtro?.usuario}>
                                                            {audOtro?.iniciales}
                                                        </span>
                                                    )}
                                                </label>
                                            );
                                        })
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="apm-empty apm-empty--center">
                                <UserIcon />
                                Selecciona un auditor para ver los pasillos
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Footer ── */}
                <div className="ram-footer">
                    <button
                        type="button"
                        className="ram-btn ram-btn--primary"
                        onClick={handleGuardar}
                        disabled={saving || loading}
                    >
                        {saving ? <span className="ram-btn__spinner" /> : <SaveIcon />}
                        {saving ? "Guardando..." : "Guardar"}
                    </button>
                </div>

            </PlantillaScreen>
        </div>
    );
};

export default AsignacionPasillosModal;
